// Lumbar Spine Template Configuration

export const lumbarTemplate = {
  id: 'LUMBAR_SPINE',
  name: 'Lumbar Spine',
  icon: '🦴',
  dominantSide: 'none',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Side Involved:',
      options: ['Right', 'Left', 'Central', 'Bilateral'],
      required: true
    },

    mechanism: {
      type: 'checkbox-multiple',
      label: 'How did your pain start?',
      options: [
        'Lifting something heavy',
        'Twisting movement',
        'Bending forward',
        'Fall',
        'Car accident',
        'It came on gradually (no specific event)',
        'Woke up with pain',
        'After sitting or standing for a long time',
        'Other'
      ]
    },

    mechanismOther: {
      type: 'text',
      label: 'Please describe what happened:',
      placeholder: 'Tell us how you were injured',
      showWhen: { field: 'mechanism', includes: 'Other' }
    },

    painSite: {
      type: 'text',
      label: 'Where is your pain?',
      placeholder: 'e.g., Lower back, buttock, back of thigh, calf, foot',
      section: 'pain'
    },

    painRadiation: {
      type: 'textarea',
      label: 'Does your pain spread anywhere else?',
      placeholder: 'Describe where the pain travels to, e.g., "Down the back of my right leg to my foot"',
      rows: 2,
      section: 'pain'
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Aching', 'Stabbing', 'Burning', 'Shooting', 'Sharp', 'Dull', 'Like an electric shock'],
      section: 'pain'
    },

    aggravatingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Bending forward',
        'Lifting things',
        'Sitting for a long time',
        'Standing for a long time',
        'Coughing or sneezing',
        'Walking',
        'Twisting',
        'First movements in the morning',
        'Lying flat',
        'Other'
      ],
      section: 'pain'
    },

    aggravatingFactorsOther: {
      type: 'text',
      label: 'Other aggravating factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'aggravatingFactors', includes: 'Other' }
    },

    relievingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain better?',
      options: [
        'Rest',
        'Lying down',
        'Walking',
        'Certain positions',
        'Pain medication',
        'Heat',
        'Ice',
        'Stretching',
        'Other'
      ],
      section: 'pain'
    },

    relievingFactorsOther: {
      type: 'text',
      label: 'Other relieving factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'relievingFactors', includes: 'Other' }
    },

    associatedSymptoms: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these other symptoms?',
      options: [
        'Numbness',
        'Tingling or pins and needles',
        'Weakness',
        'Leg cramps',
        'Foot drop (trouble lifting your foot)'
      ],
      section: 'pain'
    },

    injuryDate: {
      type: 'date',
      label: 'Date of Onset / Injury:',
      required: true
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Describe how the pain started:',
      placeholder: 'Describe the circumstances and how your symptoms began',
      rows: 3,
      required: false
    },

    // Treatment fields
    hadPhysio: {
      type: 'radio',
      label: 'Have you had physiotherapy for this injury?',
      options: ['Yes', 'No']
    },

    physioWhere: {
      type: 'text',
      label: 'Where did you have physiotherapy?',
      placeholder: 'e.g., PhysioFirst Hamilton, ACC Physio Clinic',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWho: {
      type: 'text',
      label: 'Who was your physiotherapist?',
      placeholder: 'e.g., John Smith, Sarah Jones',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWhat: {
      type: 'textarea',
      label: 'What treatment did they provide?',
      placeholder: 'e.g., Exercises, massage, dry needling, manipulation, McKenzie exercises',
      rows: 2,
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioHelped: {
      type: 'radio',
      label: 'Did the physiotherapy help?',
      options: ['Yes, significantly', 'Yes, somewhat', 'No improvement', 'Made it worse'],
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    medications: {
      type: 'textarea',
      label: 'What medications have you tried?',
      placeholder: 'e.g., Paracetamol, ibuprofen, voltaren, codeine, tramadol - did they help?',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any imaging (X-ray, MRI, CT)?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'checkbox-multiple',
      label: 'What type of imaging have you had?',
      options: ['X-ray', 'MRI', 'CT scan', 'Bone scan', 'Other'],
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhere: {
      type: 'text',
      label: 'Where was the imaging done?',
      placeholder: 'e.g., Waikato Hospital, Pacific Radiology',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhen: {
      type: 'date',
      label: 'When was the imaging done?',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What did the scan show?',
      placeholder: 'e.g., Normal, disc bulge, wear and tear changes (if you know)',
      rows: 2,
      showWhen: { field: 'imaging', includes: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. Injury Details / Onset',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'mechanism', 'mechanismOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Pain History',
      component: 'GenericSection',
      enabled: true,
      fields: ['painSite', 'painCharacter', 'painSeverity', 'painRadiation',
               'aggravatingFactors', 'aggravatingFactorsOther', 'relievingFactors', 'relievingFactorsOther', 'associatedSymptoms'],
      emphasize: ['painRadiation']
    },
    {
      id: 'stiffness',
      title: '4. Stiffness / Restricted Movement',
      component: 'StiffnessSection',
      enabled: true,
      movements: [
        { id: 'flexion', label: 'Flexion (bending forward)', icon: '🔽' },
        { id: 'extension', label: 'Extension (bending backward)', icon: '🔼' },
        { id: 'sideBending', label: 'Side bending', icon: '↔️' },
        { id: 'rotation', label: 'Rotation (twisting)', icon: '🔄' }
      ],
      morningStiffness: true,
      morningStiffnessDuration: true
    },
    {
      id: 'neurological',
      title: '5. Nerve Symptoms',
      component: 'NeurologicalSection',
      enabled: true,
      critical: true,
      fields: [
        {
          id: 'sensorySymptoms',
          type: 'textarea',
          label: 'Do you have any numbness, tingling, or pins and needles?',
          placeholder: 'Tell us where you feel it, for example:\n- Inner leg and ankle\n- Outer leg, top of foot, big toe\n- Bottom of foot, little toes, back of calf',
          rows: 4
        },
        {
          id: 'motorSymptoms',
          type: 'textarea',
          label: 'Do you have any weakness in your legs?',
          placeholder: 'For example:\n- Trouble lifting your foot up\n- Difficulty walking on your heels\n- Difficulty walking on your toes\n- Trouble standing up from a chair\n- Difficulty with stairs',
          rows: 4
        }
      ]
    },
    {
      id: 'redFlags',
      title: '⚠️ Important Health Questions',
      component: 'RedFlagsSection',
      enabled: true,
      critical: true,
      emergency: true,
      intro: 'Please answer these important questions honestly. They help us check for serious conditions:',
      fields: [
        {
          id: 'urinaryRetention',
          type: 'radio',
          label: 'Are you having trouble peeing, or unable to pee at all?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 10
        },
        {
          id: 'urinaryIncontinence',
          type: 'radio',
          label: 'Have you lost control of your bladder (wetting yourself by accident)?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 10
        },
        {
          id: 'fecalIncontinence',
          type: 'radio',
          label: 'Have you lost control of your bowels (soiling yourself by accident)?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 10
        },
        {
          id: 'saddleAnesthesia',
          type: 'radio',
          label: 'Do you have numbness in your bottom, groin, or inner thighs?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 10,
          note: 'The area that would touch a saddle if you were riding a bike'
        },
        {
          id: 'bilateralLegWeakness',
          type: 'radio',
          label: 'Do you have weakness in BOTH legs?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 8
        },
        {
          id: 'bilateralLegNumbness',
          type: 'radio',
          label: 'Do you have numbness or tingling in BOTH legs?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 7
        },
        {
          id: 'progressiveNeurological',
          type: 'radio',
          label: 'Are your symptoms getting worse quickly (over days)?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 8
        },
        {
          id: 'unableToWalk',
          type: 'radio',
          label: 'Are you unable to walk or stand because of your symptoms?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 7
        },
        {
          id: 'historyOfCancer',
          type: 'radio',
          label: 'Have you ever had cancer?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'unexplainedWeightLoss',
          type: 'radio',
          label: 'Have you lost weight without trying (more than 5kg in 6 months)?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'feverNightSweats',
          type: 'radio',
          label: 'Do you have a fever or wake up sweating at night?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'significantTrauma',
          type: 'radio',
          label: 'Did you have a serious accident (like a bad fall or car crash)?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 6
        },
        {
          id: 'ageOver50NewOnset',
          type: 'radio',
          label: 'Are you over 50 years old with back pain that just started?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 3
        },
        {
          id: 'prolongedSteroidUse',
          type: 'radio',
          label: 'Have you taken steroid tablets (like prednisone) for a long time?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 4
        },
        {
          id: 'immunosuppression',
          type: 'radio',
          label: 'Do you have a weakened immune system (HIV, chemotherapy, organ transplant)?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'ivDrugUse',
          type: 'radio',
          label: 'Do you inject drugs?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'painWorseWhenLying',
          type: 'radio',
          label: 'Is your pain worse when lying down, or do you have severe pain at night that nothing helps?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 4
        }
      ],
      alerts: {
        critical: {
          threshold: 10,
          message: '🚨 URGENT - Please contact us immediately or go to the Emergency Department',
          color: 'red',
          triggerFields: ['urinaryRetention', 'urinaryIncontinence', 'fecalIncontinence', 'saddleAnesthesia']
        },
        urgent: {
          threshold: 7,
          message: '⚠️ We need to see you soon - Please contact us today',
          color: 'orange'
        },
        concerning: {
          threshold: 5,
          message: '⚡ We will review your answers carefully',
          color: 'yellow'
        }
      }
    },
    {
      id: 'functional',
      title: '6. How Your Pain Affects Daily Life',
      component: 'FunctionalLimitationSection',
      enabled: true,
      activities: [
        'Walking',
        'Standing',
        'Sitting',
        'Lifting things',
        'Climbing stairs',
        'Getting in and out of a car',
        'Driving',
        'Sleeping',
        'Work',
        'Housework or gardening'
      ],
      additionalQuestions: [
        {
          id: 'claudicationDistance',
          label: 'How far can you walk before leg pain makes you stop?',
          type: 'text',
          placeholder: 'e.g., 50 metres, 100 metres, 500 metres, no limit'
        },
        {
          id: 'gaitDisturbance',
          label: 'Have you noticed any changes in how you walk?',
          type: 'radio',
          options: ['Yes', 'No']
        },
        {
          id: 'gaitDescription',
          label: 'Describe how your walking has changed:',
          type: 'text',
          placeholder: 'e.g., Limping, dragging foot, unsteady',
          conditional: { field: 'gaitDisturbance', value: 'Yes' }
        }
      ]
    },
    {
      id: 'deformity',
      title: '7. Changes in Your Back\'s Shape',
      component: 'DeformitySection',
      enabled: true,
      fields: [
        {
          id: 'noticedDeformity',
          type: 'text',
          label: 'Have you noticed your back looks different or you\'re leaning to one side?',
          placeholder: 'e.g., Leaning to one side, curved spine, hunched forward'
        },
        {
          id: 'deformityDuration',
          type: 'text',
          label: 'How long have you noticed this, and is it getting worse?',
          placeholder: 'e.g., Started 6 months ago and getting worse, happened suddenly with the pain'
        }
      ]
    },
    {
      id: 'treatment',
      title: '8. Treatment to Date',
      component: 'GenericSection',
      enabled: true,
      fields: ['hadPhysio', 'physioWhere', 'physioWho', 'physioWhat', 'physioHelped', 'medications', 'imaging', 'imagingType', 'imagingWhere', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'pastHistory',
      title: '9. Your Back History',
      component: 'PastHistorySection',
      enabled: true,
      specificQuestions: [
        {
          id: 'previousBackInjuries',
          label: 'Have you had any previous back injuries or back surgery?',
          type: 'textarea',
          placeholder: 'Please include what happened, when, and what treatment you had',
          rows: 3
        },
        {
          id: 'significantTraumaHistory',
          label: 'Have you had any serious accidents that affected your back?',
          type: 'textarea',
          placeholder: 'e.g., Falls, car accidents, sports injuries',
          rows: 2
        },
        {
          id: 'similarEpisodes',
          label: 'Have you had similar back pain before?',
          type: 'textarea',
          placeholder: 'When did it happen, how long did it last, what helped?',
          rows: 3
        },
        {
          id: 'comorbidities',
          label: 'Do you have any of these health conditions?',
          type: 'textarea',
          placeholder: 'e.g., Diabetes, arthritis, osteoporosis (weak bones), inflammatory arthritis',
          rows: 2
        }
      ]
    },
    {
      id: 'medical',
      title: '10. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true,
      additionalFields: [
        {
          id: 'psychosocialFactors',
          label: 'How is this pain affecting your mood and daily life?',
          type: 'textarea',
          placeholder: 'e.g., Feeling stressed, anxious, low mood, affecting work or relationships',
          rows: 3
        },
        {
          id: 'workDescription',
          label: 'What does your job involve?',
          type: 'textarea',
          placeholder: 'e.g., Heavy lifting, sitting at a desk all day, driving, standing',
          rows: 2
        }
      ]
    },    {
      id: 'exerciseVitalSign',
      title: '11. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '12. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Lifting heavy object': 'lifting a heavy object',
      'Twisting movement': 'a twisting movement',
      'Bending forward': 'bending forward',
      'Fall': 'a fall',
      'Motor vehicle accident': 'a motor vehicle accident',
      'Gradual onset (no specific event)': 'a gradual onset with no specific precipitating event',
      'Woke up with pain': 'waking with pain',
      'After prolonged sitting/standing': 'after prolonged sitting/standing'
    },

    examinationGuidance: `
Based on the clinical information provided, examination findings would typically include:

INSPECTION: Posture assessment, spinal alignment, deformity (scoliosis, loss of lumbar lordosis), gait pattern, ability to heel and toe walk.

PALPATION: Tenderness over spinous processes, paraspinal muscles, sacroiliac joints. Muscle spasm.

RANGE OF MOTION:
- Lumbar flexion (Schober's test)
- Extension, side bending, rotation
- Noting pain reproduction and limitation

NEUROLOGICAL EXAMINATION (Critical):
SENSORY: Dermatomal testing (L4, L5, S1)
- L4: Medial shin and ankle
- L5: Lateral calf, dorsum of foot, first web space
- S1: Lateral foot, little toe

MOTOR: Myotomal testing
- L4: Ankle dorsiflexion, knee extension
- L5: Great toe extension, ankle eversion
- S1: Ankle plantarflexion, hip extension

REFLEXES:
- Knee jerk (L3/4)
- Ankle jerk (S1)

SPECIAL TESTS:
- Straight leg raise (Lasègue test) - tension signs
- Crossed straight leg raise
- Femoral stretch test (L2/3/4)
- Slump test

RED FLAG EXAMINATION (if indicated):
- Perianal sensation
- Anal tone (per rectal examination if cauda equina suspected)

GAIT: Observation for antalgic gait, foot drop, Trendelenburg sign
`,

    commonDiagnoses: [
      'Lumbar disc herniation with radiculopathy (L4, L5, or S1)',
      'Mechanical low back pain',
      'Lumbar facet joint syndrome',
      'Lumbar spinal stenosis (neurogenic claudication)',
      'Sacroiliac joint dysfunction',
      'Lumbar muscle strain',
      'Spondylolisthesis',
      'Degenerative disc disease',
      'Lumbar spondylosis'
    ],

    emergencyDiagnoses: [
      'Cauda equina syndrome',
      'Spinal cord compression',
      'Spinal infection (discitis, epidural abscess, osteomyelitis)',
      'Spinal metastases',
      'Vertebral fracture',
      'Abdominal aortic aneurysm (referred pain)'
    ],

    redFlagNote: `
CRITICAL: If any cauda equina red flags are present (urinary retention, fecal incontinence, saddle anesthesia, bilateral leg symptoms), this patient requires URGENT MRI and surgical assessment within 24-48 hours. Delayed decompression (>48 hours) is associated with poor functional outcomes.
`
  }
};

export default lumbarTemplate;
